import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Plus, Trash2, Save, Loader2, Tag, AlertTriangle, Edit3, Check,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { blogAPI } from '../../services/api';

/**
 * BlogCategories — modal for CRUD on blog categories.
 *
 * Usage:
 *   <BlogCategories
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     onChanged={() => refetchCategoriesInParent()}
 *   />
 *
 * Backend error codes handled:
 *   SLUG_CONFLICT (409), INVALID_SLUG, NOT_FOUND.
 *
 * The server cascades untagging when a category is removed (warns the user
 * if postsCount > 0).
 */

const EMPTY_DRAFT = {
  slug: '',
  name: { ar: '', en: '' },
  description: { ar: '', en: '' },
  order: 0,
  isActive: true,
};

function extractMessage(err, fallback) {
  return err?.response?.data?.message || fallback;
}

function mapErrorToArabic(err, fallback) {
  const code = err?.response?.data?.code;
  if (code === 'SLUG_CONFLICT') return 'الرابط مستخدم مسبقاً — اختر واحداً آخر';
  if (code === 'INVALID_SLUG') return 'الرابط غير صالح — حروف صغيرة وأرقام وشرطات فقط';
  return extractMessage(err, fallback);
}

export default function BlogCategories({ open, onClose, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [newDraft, setNewDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const fetchItems = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    setLoadError(null);
    try {
      const resp = await blogAPI.listCategories();
      const data = resp.data?.data || resp.data;
      if (!aliveRef.current) return;
      const list = Array.isArray(data?.items) ? data.items : [];
      list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setItems(list);
    } catch (err) {
      if (!aliveRef.current) return;
      setLoadError(extractMessage(err, 'فشل تحميل التصنيفات'));
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, [open]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  useEffect(() => {
    if (!open) {
      setEditingId(null);
      setConfirmDelete(null);
      setNewDraft(EMPTY_DRAFT);
    }
  }, [open]);

  const startEdit = (cat) => {
    setEditingId(cat._id);
    setDraft({
      slug: cat.slug || '',
      name: { ar: cat.name?.ar || '', en: cat.name?.en || '' },
      description: { ar: cat.description?.ar || '', en: cat.description?.en || '' },
      order: Number(cat.order) || 0,
      isActive: cat.isActive !== false,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(EMPTY_DRAFT);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    if (!draft.name.ar && !draft.name.en) {
      toast.error('أدخل اسم التصنيف بالعربية أو الإنجليزية');
      return;
    }
    setSaving(true);
    try {
      await blogAPI.updateCategory(editingId, {
        slug: draft.slug || undefined,
        name: draft.name,
        description: draft.description,
        order: Number(draft.order) || 0,
        isActive: !!draft.isActive,
      });
      toast.success('تم حفظ التصنيف');
      cancelEdit();
      await fetchItems();
      onChanged?.();
    } catch (err) {
      toast.error(mapErrorToArabic(err, 'فشل حفظ التصنيف'));
    } finally {
      if (aliveRef.current) setSaving(false);
    }
  };

  const createNew = async () => {
    if (!newDraft.name.ar && !newDraft.name.en) {
      toast.error('أدخل اسم التصنيف بالعربية أو الإنجليزية');
      return;
    }
    setCreating(true);
    try {
      await blogAPI.createCategory({
        slug: newDraft.slug || undefined,
        name: newDraft.name,
        description: newDraft.description,
        order: Number(newDraft.order) || 0,
        isActive: !!newDraft.isActive,
      });
      toast.success('تم إنشاء التصنيف');
      setNewDraft(EMPTY_DRAFT);
      await fetchItems();
      onChanged?.();
    } catch (err) {
      toast.error(mapErrorToArabic(err, 'فشل إنشاء التصنيف'));
    } finally {
      if (aliveRef.current) setCreating(false);
    }
  };

  const removeCategory = async (cat) => {
    setDeletingId(cat._id);
    try {
      await blogAPI.removeCategory(cat._id);
      toast.success('تم حذف التصنيف');
      setConfirmDelete(null);
      await fetchItems();
      onChanged?.();
    } catch (err) {
      toast.error(extractMessage(err, 'فشل الحذف'));
    } finally {
      if (aliveRef.current) setDeletingId(null);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-gray-900/60 flex items-center justify-center p-4"
        onClick={onClose}
        dir="rtl"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-sm">
                <Tag className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-800">تصنيفات المدونة</h2>
                <p className="text-xs text-gray-500 mt-0.5">أنشئ وحرّر تصنيفات مقالاتك</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {/* Add new row */}
            <div className="rounded-2xl border border-dashed border-blue-200 bg-blue-50/40 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Plus className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-semibold text-blue-800">إضافة تصنيف جديد</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <input
                  type="text"
                  value={newDraft.name.ar}
                  onChange={(e) => setNewDraft((p) => ({ ...p, name: { ...p.name, ar: e.target.value } }))}
                  placeholder="الاسم (عربي)"
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                />
                <input
                  type="text"
                  value={newDraft.name.en}
                  onChange={(e) => setNewDraft((p) => ({ ...p, name: { ...p.name, en: e.target.value } }))}
                  placeholder="Name (English)"
                  dir="ltr"
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                />
                <input
                  type="text"
                  value={newDraft.slug}
                  onChange={(e) => setNewDraft((p) => ({ ...p, slug: e.target.value }))}
                  placeholder="الرابط (اختياري — يُنشأ تلقائياً)"
                  dir="ltr"
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 font-mono bg-white"
                />
                <input
                  type="number"
                  value={newDraft.order}
                  onChange={(e) => setNewDraft((p) => ({ ...p, order: e.target.value }))}
                  placeholder="الترتيب (0)"
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
                />
              </div>
              <div className="flex items-center justify-between mt-3">
                <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={!!newDraft.isActive}
                    onChange={(e) => setNewDraft((p) => ({ ...p, isActive: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  فعّال
                </label>
                <button
                  type="button"
                  onClick={createNew}
                  disabled={creating}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  إضافة
                </button>
              </div>
            </div>

            {/* Existing list */}
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : loadError ? (
              <div className="p-6 rounded-2xl border border-blue-100 bg-blue-50 text-center">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                <p className="text-sm text-blue-700">{loadError}</p>
                <button
                  onClick={fetchItems}
                  className="mt-3 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm"
                >
                  أعد المحاولة
                </button>
              </div>
            ) : items.length === 0 ? (
              <div className="py-10 text-center text-gray-400">
                <Tag className="w-10 h-10 mx-auto mb-2" />
                <p className="text-sm">لا توجد تصنيفات بعد — أضف أول تصنيف من الأعلى</p>
              </div>
            ) : (
              <div className="space-y-2">
                {items.map((cat) => {
                  const isEditing = editingId === cat._id;
                  const hasPosts = Number(cat.postsCount) > 0;
                  return (
                    <div
                      key={cat._id}
                      className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm"
                    >
                      {isEditing ? (
                        <div className="space-y-3">
                          <div className="grid sm:grid-cols-2 gap-3">
                            <input
                              type="text"
                              value={draft.name.ar}
                              onChange={(e) => setDraft((p) => ({ ...p, name: { ...p.name, ar: e.target.value } }))}
                              placeholder="الاسم (عربي)"
                              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                            />
                            <input
                              type="text"
                              value={draft.name.en}
                              onChange={(e) => setDraft((p) => ({ ...p, name: { ...p.name, en: e.target.value } }))}
                              placeholder="Name (English)"
                              dir="ltr"
                              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                            />
                            <input
                              type="text"
                              value={draft.slug}
                              onChange={(e) => setDraft((p) => ({ ...p, slug: e.target.value }))}
                              placeholder="الرابط"
                              dir="ltr"
                              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                            />
                            <input
                              type="number"
                              value={draft.order}
                              onChange={(e) => setDraft((p) => ({ ...p, order: e.target.value }))}
                              placeholder="الترتيب"
                              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                            />
                          </div>
                          <textarea
                            value={draft.description.ar}
                            onChange={(e) => setDraft((p) => ({ ...p, description: { ...p.description, ar: e.target.value } }))}
                            placeholder="وصف قصير (عربي)"
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                          />
                          <div className="flex items-center justify-between">
                            <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={!!draft.isActive}
                                onChange={(e) => setDraft((p) => ({ ...p, isActive: e.target.checked }))}
                                className="w-4 h-4 text-blue-600 rounded"
                              />
                              فعّال
                            </label>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={cancelEdit}
                                className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50"
                              >
                                إلغاء
                              </button>
                              <button
                                type="button"
                                onClick={saveEdit}
                                disabled={saving}
                                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                              >
                                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                                حفظ
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-semibold text-gray-800">
                                {cat.name?.ar || cat.name?.en || '(بدون اسم)'}
                              </span>
                              {cat.name?.en && (
                                <span className="text-xs text-gray-400" dir="ltr">{cat.name.en}</span>
                              )}
                              {!cat.isActive && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">غير فعّال</span>
                              )}
                              {hasPosts && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">
                                  {cat.postsCount} مقال
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-gray-400 mt-1">
                              <code className="font-mono" dir="ltr">/{cat.slug}</code>
                              <span>ترتيب: {cat.order ?? 0}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => startEdit(cat)}
                              className="p-2 rounded-lg text-gray-500 hover:text-blue-700 hover:bg-blue-50"
                              title="تعديل"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmDelete(cat)}
                              className="p-2 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                              title="حذف"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Confirm delete */}
          <AnimatePresence>
            {confirmDelete && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-gray-900/40 flex items-center justify-center p-4"
                onClick={() => setConfirmDelete(null)}
              >
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                  onClick={(e) => e.stopPropagation()}
                  className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl"
                >
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                      <AlertTriangle className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-gray-800">تأكيد الحذف</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        هل تريد حذف تصنيف &quot;{confirmDelete.name?.ar || confirmDelete.name?.en || confirmDelete.slug}&quot;؟
                      </p>
                      {Number(confirmDelete.postsCount) > 0 && (
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2 mt-2">
                          هناك {confirmDelete.postsCount} مقال مرتبط — سيتم إزالة التصنيف من هذه المقالات تلقائياً.
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => setConfirmDelete(null)}
                      className="px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50"
                    >
                      إلغاء
                    </button>
                    <button
                      onClick={() => removeCategory(confirmDelete)}
                      disabled={deletingId === confirmDelete._id}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      {deletingId === confirmDelete._id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                      حذف
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Footer */}
          <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
            <button
              onClick={onClose}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              <Check className="w-4 h-4" />
              تم
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
